import { html, render } from "lit";
import { CFacturaCFDIComplementoConcepto, FacturaCFDIMovimientoPago, IFacturaCFDIConceptoFiscal as IFacturaCFDIConceptoFiscal_, IFacturaCFDIConceptosFiscalesProcesados as IFacturaCFDIConceptosFiscalesProcesados_, IPagoInfoFacturarConceptoComplemento } from "../../../../data/entidad/Factura";
import { _SvFacturaCFDIProcesarConceptosFiscales } from "../../../../data/modulo/FacturaCFDI";
import { ArrayV2 } from "../../../../util/ArrayV2";
import _L, { _HttpMsgV2 } from "../../../../util/Labels";
import { Tag } from "../../../components/Tag/Tag";
import { _InfoIcon } from '../../../controlD3/InfoIcon';
import { List } from "../../../controlD3/List";
import { ModalThingsUtil as MTUtil } from "../../../controlD3/ModalThings/Utils";
import { NotificacionV2 } from "../../../controlD3/NotificacionV2";
import { UIUtilElement } from "../../../util/Element";
import { UIUtilFormat } from "../../../util/Format";
import { UIUtilViewData } from "../../../util/ViewData";
import { AlumnoEdoCtaFacturacionUtil, AlumnoEdoCtaFacturacionUtil as Util } from "./FacturacionUtil";
import "./FacturacionViewMovs.scss";

export type IFacturaCFDIConceptoFiscalAdvanced = IFacturaCFDIConceptoFiscal_ & {
    /** IdAgrupador-IdsMovs: 5-1,2,3,4 */
    _Identificador?: string;
    _Complemento?: IPagoInfoFacturarConceptoComplemento;
}
export type IFacturaCFDIConceptosFiscalesProcesadosAdvanced = Omit<IFacturaCFDIConceptosFiscalesProcesados_, "ConceptosFiscales"> & {
    /** @override */
    ConceptosFiscales: IFacturaCFDIConceptoFiscalAdvanced[];
    /** Ayuda a identificar los movimientos asociados a este cfdi
     * @readonly
    */
    _IdsMovs?: number[];
    /** "CFDI" + index */
    _NombreCFDI?: string;
}

type IAlumnoEdoCtaFacturacionViewMovsConfig = {
    /** Contenedor HTML donde se renderizará la vista */
    container: TSelectionHTML<"div">
    /** Instancia del modal que contiene la vista */
    mt: MTUtil.IModalThings
    /** ID de la escuela asociada */
    idEscuela: number
    /** ID del alumno asociado */
    idAlumno: number
    /** IDs de los pagos a consultar */
    idsPagos: number[]
    onloadData?: (firstData: boolean, success: boolean) => void
}

/**
 * Clase que implementa las funcionalidades relacionadas con la vista de movimientos de pagos
 * para la facturación de un alumno.
 */
export class AlumnoEdoCtaFacturacionViewMovs implements ViewMovimientosPagos {
    private modal: MTUtil.IModalThings<any>;
    private config: IAlumnoEdoCtaFacturacionViewMovsConfig;
    private pagosInfoListCtrl: List<IFacturaCFDIConceptosFiscalesProcesadosAdvanced>;
    private sinRegistrosComponent: AlumnoEdoCtaFacturacionUtil.SinRegistrosComponent;
    private cfdisConceptosProcesadosList: IFacturaCFDIConceptosFiscalesProcesadosAdvanced[] = [];
    private lblSeleccionadoInfo: HTMLLabelElement & { _refreshText?: () => void };
    /** Solo contiene la info seleccionada */
    private dataSelected: {
        [grupoCFDIkey: string]: {
            [identificadorConcepto: string]: Set<number>
        }
    } = {};

    constructor(config: IAlumnoEdoCtaFacturacionViewMovsConfig) {
        this.modal = config.mt;
        this.config = config;

        const { container } = config;
        let forceRequestCurp = true;

        container
            .classed("alumnoedocuenta-facturacion-movs", true)
            .text(_L("panelfinanzasedocuenta_factura.movs_seleccionapagos"))
            .style("min-height", "200px")
            .append(() => {
                this.lblSeleccionadoInfo = UIUtilElement._CreateElementFromHTML(`<label class="lbl-seleccionado-info"></label>`);
                this.lblSeleccionadoInfo._refreshText = () => {
                    this.lblSeleccionadoInfo.textContent = this.GetMovsInfo();
                }
                return this.lblSeleccionadoInfo;
            })

        // Lista de CFDI y sus conceptos fiscales procesados
        this.pagosInfoListCtrl = Util._CreateListControl<IFacturaCFDIConceptosFiscalesProcesadosAdvanced>({ addMarginTop: true })
            ._SetParent(container)
            ._SetUpdateItem((container, grupoCFDI) => {
                container.html("") // Limpia contenido previo
                container.append(() => {
                    const tag = new Tag({ Value: grupoCFDI._NombreCFDI });
                    const cfdiIsSelected = !!this.dataSelected[grupoCFDI.GrupoCFDI];
                    if (cfdiIsSelected) tag._ControlContainer.classList.add("tag-selected");
                    tag._ControlContainer.onclick = () => {
                        this.SelectCFDIsByKeys(grupoCFDI.GrupoCFDI, !cfdiIsSelected);
                        this.RefreshView();
                    }
                    return tag._ControlContainer;
                })
                container.append(() => {
                    const tieneClaveFormaPago = !!grupoCFDI.ClaveFormaPagoSAT;
                    const cfdiDescription = UIUtilElement._CreateElementFromHTML(`<div>
                        <div name="cfdi-forma-pago-div">
                            <b>${_L("panelfinanzasedocuenta_factura.forma_pago")}:</b>
                            ${tieneClaveFormaPago ? `${grupoCFDI.ClaveFormaPagoSAT} - ${grupoCFDI.NombreFormaPagoSAT}` : _L("general.por_definir")}
                        </div>
                        <div>
                            <b>${_L("panelfinanzasedocuenta_factura.fecha_pago")}:</b>
                            ${grupoCFDI.FechaPagoFmt}
                        </div>
                    </div>`);
                    if (!tieneClaveFormaPago) {
                        _InfoIcon(cfdiDescription.querySelector("div[name='cfdi-forma-pago-div']") as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                    }
                    return cfdiDescription;
                })
                container.append(() => {
                    // Lista de conceptos fiscales
                    const conceptosList = UIUtilElement._CreateElementFromHTML<HTMLDivElement>(`<div><b>${_L("panelfinanzasedocuenta_factura.conceptos")}:</b></div>`);
                    new List<IFacturaCFDIConceptoFiscalAdvanced>()
                        ._SetParent(conceptosList)
                        ._SetUpdateItem((itemDivConcepto, conceptoF) => {
                            const conceptoIsSelected = !!this.dataSelected[grupoCFDI.GrupoCFDI] && !!this.dataSelected[grupoCFDI.GrupoCFDI][conceptoF._Identificador];
                            const conceptoContent = itemDivConcepto.append("div");
                            const tieneClaveProdServ = !!conceptoF.ClaveProdServSAT;
                            const tieneClaveUnidad = !!conceptoF.ClaveUnidadSAT;
                            const usaComplementoIEDU = conceptoF.ComplementoRequerido == CFacturaCFDIComplementoConcepto.IEDU;
                            const conceptoHtml = html`
                                <div class="item">${conceptoF.Descripcion}</div>
                                ${!conceptoF.EsFacturable
                                    ? `<div style="color: var(--color_app_red1);">${_L("panelfinanzasedocuenta_factura.no_facturable")}</div>`
                                    : ""
                                }
                                <div>
                                    <i name="claveprodserv-div">
                                        ${_L("panelfinanzasedocuenta_factura.claveprodserv")}:
                                        ${tieneClaveProdServ ? `${conceptoF.ClaveProdServSAT} - ${conceptoF.NombreProdServSAT}` : _L("general.por_definir")}
                                    </i>
                                </div>
                                <div>
                                    <i name="claveunidad-div">
                                        ${_L("panelfinanzasedocuenta_factura.unidad")}:
                                        ${tieneClaveUnidad ? `${conceptoF.ClaveUnidadSAT} - ${conceptoF.NombreUnidadSAT}` : _L("general.por_definir")}
                                    </i>
                                </div>
                                <div>
                                    <i name="complemento-div">
                                        ${_L("panelfinanzasedocuenta_factura.req_complemento")}:
                                        ${UIUtilViewData._GetStr_FacturaCFDIComplementoConcepto(conceptoF.ComplementoRequerido) || _L("general.nodisponible")}
                                        ${usaComplementoIEDU ? html`
                                                <i class="complemento-loading-div">
                                                    <wc-spinner dim="12px"></wc-spinner>
                                                </i>` : null
                                }
                                    </i>
                                </div>
                            `;
                            Util._ViewItemListCheck(conceptoContent, conceptoIsSelected, conceptoHtml)
                                .call((section) => {
                                    if (!tieneClaveProdServ) {
                                        console.warn("nodo claveprodserv-div", section.select("i[name='claveprodserv-div']").node());
                                        _InfoIcon(section.select("i[name='claveprodserv-div']").node() as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                                    }
                                    if (!tieneClaveUnidad) {
                                        _InfoIcon(section.select("i[name='claveunidad-div']").node() as HTMLElement, _L("panelfinanzasedocuenta_factura.tooltip_info_definir"));
                                    }

                                    if (usaComplementoIEDU) {
                                        const _forceRequestCurp = forceRequestCurp;
                                        forceRequestCurp = false; // Solo se fuerza la primera vez
                                        Util._ValidaInfoRequeridaComplementoIEDU(this.config.idAlumno, {
                                            forceRequestCurp: _forceRequestCurp,
                                            shouldNotify: false
                                        }).then(res => {
                                            const complementoLoadInfoNode: HTMLElement = section.select<HTMLElement>("i[name='complemento-div'] .complemento-loading-div").node();
                                            complementoLoadInfoNode.innerHTML = "";
                                            if (!res.valid) {
                                                const onClick = e => {
                                                    e.stopPropagation();
                                                    Util._ValidaInfoRequeridaComplementoIEDU(this.config.idAlumno, {
                                                        shouldNotify: true,
                                                        onChangePath: () => config.mt.Modal._Ocultar() // Cierra el modal principal (GenerarCFDI)
                                                    });
                                                }
                                                render(html`
                                                    <br>
                                                    <a href="javascript:void(0);">
                                                        <i style="font-weight: bold; color: var(--color_app_red1); cursor: pointer;" @click=${onClick}>
                                                            ${_L("panelfinanzasedocuenta_factura.compl_iedu_info_falta")}
                                                        </i>
                                                    </a>`
                                                    , complementoLoadInfoNode);
                                            }
                                        });
                                    }
                                })
                                .node().onclick = () => {
                                    this.SelectConceptosByKeys(grupoCFDI.GrupoCFDI, conceptoF._Identificador, !conceptoIsSelected);
                                    this.RefreshView();
                                }

                            // Detalles de los movimientos asociados al concepto
                            if (conceptoF.MovimientoFinanza?.length) {
                                const movsAgrupadosSection = itemDivConcepto.append("div").attr("class", "movimientos-agrupados-section");
                                movsAgrupadosSection.append("div").append("b").text(_L("panelfinanzasedocuenta_factura.movs_asociados_a_concepto"));
                                new List<FacturaCFDIMovimientoPago>()
                                    ._SetParent(movsAgrupadosSection)
                                    ._SetUpdateItem((itemDivMov, movPago) => {
                                        const movIsSelected = conceptoIsSelected && this.dataSelected[grupoCFDI.GrupoCFDI][conceptoF._Identificador]?.has(movPago.Id);
                                        const movContent = itemDivMov.append("div");
                                        const movHtml = `
                                            <div class="item">${movPago.NombreCargo}</div>
                                            ${!movPago.EsFacturable
                                                ? `<div style="color: var(--color_app_red1);">${_L("panelfinanzasedocuenta_factura.no_facturable")}</div>`
                                                : ""
                                            }
                                            <div><i>${_L("panelfinanzasedocuenta_factura.periodo")}: ${movPago.NombrePeriodo}</i></div>
                                            <div><i>${_L("panelfinanzasedocuenta_factura.monto")}: ${UIUtilFormat._CurrencyFmt(movPago.Monto)}</i></div>
                                        `;
                                        Util._ViewItemListCheck(movContent, movIsSelected, movHtml)
                                            .node().onclick = () => {
                                                this.SelectMovsByKeys(grupoCFDI.GrupoCFDI, conceptoF._Identificador, movPago.Id, movPago.EsFacturable, !movIsSelected);
                                                this.RefreshView();
                                            }
                                    })
                                    ._SetItems(conceptoF.MovimientoFinanza)
                                    ._ListContainerSelection.classed("list-movimientos-pagos", true)
                            }
                        })
                        ._SetItems(grupoCFDI.ConceptosFiscales)
                        ._ListContainerSelection.classed("list-conceptos-cfdi", true)
                    return conceptosList;
                })
            })
        this.pagosInfoListCtrl._ListContainerSelection.classed("list-cfdis", true)

        /** Componente para mostrar un mensaje cuando no hay registros */
        this.sinRegistrosComponent = Util._CreateSinRegistrosComponent(container)

        this.ConsultaMovimientosProcesar() // Realiza la consulta inicial
    }

    // ****************************************************************
    // PUBLIC METHODS
    // ****************************************************************

    //#region PUBLIC METHODS

    /** Obtiene la cantidad de CFDIs (pre-procesados) seleccionados */
    public GetCFDISelectedCount(): number {
        return Object.keys(this.dataSelected).length;
    }

    /** Re-consulta los conceptos fiscales procesados de los CFDIs seleccionados */
    public ConsultaCFDIConceptosFiscalesProcesados(): Promise<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]> {
        const idsMovsSelected = this.GetIdsMovsSelected();
        return _SvFacturaCFDIProcesarConceptosFiscales(this.config.idEscuela, this.config.idAlumno, idsMovsSelected)
            .then<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]>(async res => {
                return new ArrayV2(...(res.Datos || []))
                    ._MapAwait((cfdiC, i) => (
                        this.PreparaCFDIConceptos(cfdiC, (i + 1).toString())
                    ));
            });
    }

    public async IsValid(): Promise<boolean> {
        // Valida que haya movimiento(s) seleccionados
        let isValid = Object.keys(this.dataSelected).length > 0;
        if (!isValid) {
            NotificacionV2._Mostrar(_L("panelfinanzasedocuenta_factura.movs_seleccionapagos"), "ADVERTENCIA");
        }
        else {
            // _ValidaInfoRequeridaComplementoIEDU si al menos un concepto en grupoCFDIs seleccionados tengan ComplementoRequerido == Complemento.IEDU
            for (const grupoCFDIKey in this.dataSelected) {
                const grupoCFDI = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDIKey);
                if (!grupoCFDI) continue;
                for (const identificadorConcepto in this.dataSelected[grupoCFDIKey]) {
                    const conceptoF = grupoCFDI.ConceptosFiscales.find(c => c._Identificador == identificadorConcepto);
                    if (!conceptoF) continue;
                    if (conceptoF.ComplementoRequerido == CFacturaCFDIComplementoConcepto.IEDU) {
                        isValid = await Util._ValidaInfoRequeridaComplementoIEDU(this.config.idAlumno, {
                            shouldNotify: true,
                            onChangePath: () => this.config.mt.Modal._Ocultar(), // Cierra el modal principal (GenerarCFDI)
                        }).then(res => res.valid);
                        if (!isValid) break;
                    }
                }
                if (!isValid) break;
            }
        }
        return isValid;
    }

    public SelectMovs(ids: number[]): this {
        this.SelectMovsByIds(ids, true);
        this.RefreshView();
        return this;
    }

    public SelectAll(): this {
        this.SelectAllMovs(true);
        this.RefreshView();
        return this;
    }

    public ReloadData(): this {
        this.ConsultaMovimientosProcesar();
        return this;
    }

    public RefreshView(): this {
        this.pagosInfoListCtrl._RefreshList();
        this.lblSeleccionadoInfo._refreshText();
        return this;
    }

    //#endregion

    // ****************************************************************
    // PRIVATE METHODS
    // ****************************************************************

    //#region PRIVATE METHODS

    private GetMovsInfo(): string {
        let nCFDIs = 0;
        let nConceptos = 0;
        let nMovs = 0;
        for (const grupoCFDIKey in this.dataSelected) {
            nCFDIs++;
            for (const identificadorConcepto in this.dataSelected[grupoCFDIKey]) {
                nConceptos++;
                nMovs += this.dataSelected[grupoCFDIKey][identificadorConcepto].size;
            }
        }
        return _L("panelfinanzasedocuenta_factura.movs_seleccionados_info", nCFDIs, nMovs, nConceptos)
    }

    private GetIdsMovsSelected(): number[] {
        const ids: number[] = [];
        for (const grupoCFDIKey in this.dataSelected) {
            for (const identificadorConcepto in this.dataSelected[grupoCFDIKey]) {
                ids.push(...this.dataSelected[grupoCFDIKey][identificadorConcepto]);
            }
        }
        return ids;
    }

    private GetMovInfo(idMov: number): { grupoCFDIKey: string, identificadorConcepto: string, esFacturable: boolean } | null {
        for (const cfdiconceptos of this.cfdisConceptosProcesadosList) {
            for (let i = 0; i < cfdiconceptos.ConceptosFiscales.length; i++) {
                const c = cfdiconceptos.ConceptosFiscales[i];
                const mov = c.MovimientoFinanza.find(mov => mov.Id == idMov);
                if (mov) {
                    return { grupoCFDIKey: cfdiconceptos.GrupoCFDI, identificadorConcepto: c._Identificador, esFacturable: mov.EsFacturable }
                }
            }
        }
        return null
    }

    private SelectCFDIsByKeys(grupoCFDIkey: string, selected: boolean) {
        if (!this.dataSelected[grupoCFDIkey] && !selected) return;
        if (selected) {
            const grupoCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDIkey);
            if (!grupoCFDI) return;
            grupoCFDI.ConceptosFiscales.forEach(concepto => {
                concepto.MovimientoFinanza.forEach(mov => {
                    this.SelectMovsByKeys(grupoCFDIkey, concepto._Identificador, mov.Id, mov.EsFacturable, true);
                });
            });
        } else {
            delete this.dataSelected[grupoCFDIkey];
            // console.warn("dataSelected", this.dataSelected, selected, grupoCFDIkey, "ALL", "ALL");
        }
    }

    private SelectConceptosByKeys(grupoCFDIkey: string, identificadorConcepto: string, selected: boolean) {
        if (!this.dataSelected[grupoCFDIkey]?.[identificadorConcepto] && !selected) return;
        if (selected) {
            const grupoCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == grupoCFDIkey);
            if (!grupoCFDI) return;
            const concepto: IFacturaCFDIConceptoFiscalAdvanced = grupoCFDI.ConceptosFiscales.find(c => c._Identificador == identificadorConcepto);
            if (!concepto) return;
            concepto.MovimientoFinanza.forEach(mov => {
                this.SelectMovsByKeys(grupoCFDIkey, identificadorConcepto, mov.Id, mov.EsFacturable, true);
            });
        } else {
            if (this.dataSelected[grupoCFDIkey] && this.dataSelected[grupoCFDIkey][identificadorConcepto]) {
                delete this.dataSelected[grupoCFDIkey][identificadorConcepto];
                if (!Object.keys(this.dataSelected[grupoCFDIkey]).length)
                    delete this.dataSelected[grupoCFDIkey];
                // console.warn("dataSelected", this.dataSelected, selected, grupoCFDI, identificadorConcepto, "ALL");
            }
        }
    }

    /**
     * Selecciona o deselecciona un movimiento específico utilizando claves compuestas.
     * @param grupoCFDIKey Llave de Grupo CFDI al que pertenece el movimiento.
     * @param identificadorConcepto Identificador del concepto.
     * @param idMov ID del movimiento a seleccionar o deseleccionar.
     * @param esFacturable Indica si el movimiento es facturable.
     * @param selected Indica si se debe seleccionar (true) o deseleccionar (false) el movimiento.
     */
    private SelectMovsByKeys(grupoCFDIKey: string, identificadorConcepto: string, idMov: number, esFacturable: boolean, selected: boolean) {
        if (selected) {
            if (esFacturable) {
                if (!this.dataSelected[grupoCFDIKey])
                    this.dataSelected[grupoCFDIKey] = {};
                if (!this.dataSelected[grupoCFDIKey][identificadorConcepto])
                    this.dataSelected[grupoCFDIKey][identificadorConcepto] = new Set();
                this.dataSelected[grupoCFDIKey][identificadorConcepto].add(idMov);
            }
        } else {
            if (this.dataSelected[grupoCFDIKey] && this.dataSelected[grupoCFDIKey][identificadorConcepto]) {
                this.dataSelected[grupoCFDIKey][identificadorConcepto].delete(idMov);
                if (!this.dataSelected[grupoCFDIKey][identificadorConcepto].size)
                    delete this.dataSelected[grupoCFDIKey][identificadorConcepto];
                if (!Object.keys(this.dataSelected[grupoCFDIKey]).length)
                    delete this.dataSelected[grupoCFDIKey];
            }
        }
        // console.warn("dataSelected", this.dataSelected, selected, grupoCFDI, identificadorConcepto, idMov);
    }

    private SelectMovsByIds(idsMovs: number[], selected: boolean) {
        idsMovs.forEach(idMov => {
            const movInfo = this.GetMovInfo(idMov);
            if (!movInfo) return;
            const { grupoCFDIKey, identificadorConcepto, esFacturable } = movInfo;
            this.SelectMovsByKeys(grupoCFDIKey, identificadorConcepto, idMov, esFacturable, selected);
        });
    }

    private SelectAllMovs(selected: boolean) {
        if (!selected) {
            this.dataSelected = {};
            return;
        }
        this.cfdisConceptosProcesadosList.forEach(cfdiconceptos => {
            cfdiconceptos.ConceptosFiscales.forEach((concepto) => {
                concepto.MovimientoFinanza.forEach(mov => {
                    if (mov.EsFacturable) {
                        if (!this.dataSelected[cfdiconceptos.GrupoCFDI])
                            this.dataSelected[cfdiconceptos.GrupoCFDI] = {};
                        if (!this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador])
                            this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador] = new Set();
                        this.dataSelected[cfdiconceptos.GrupoCFDI][concepto._Identificador].add(mov.Id);
                    }
                });
            });
        });
    }

    /** Prepara la información de los conceptos fiscales de un grupo CFDI consultado.
     * @param cfdiC Grupo CFDI cuyos conceptos fiscales procesados serán completados para view.
     * @param sufixCfdiName Sufijo para el nombre del CFDI (normalmente un índice: 1, 2, 3, ...)
     * @returns El mismo objeto `cfdiC` pero con las propiedades adicionales completadas.
     *      El complemento de cada concepto fiscal se mantiene si ya existía previamente.
     */
    private async PreparaCFDIConceptos(cfdiC: IFacturaCFDIConceptosFiscalesProcesadosAdvanced, sufixCfdiName: string): Promise<IFacturaCFDIConceptosFiscalesProcesadosAdvanced> {
        cfdiC._NombreCFDI = `${_L("panelfinanzasedocuenta_factura.cfdi")} ${sufixCfdiName}`;
        cfdiC._IdsMovs = [];
        for (const conceptoF of cfdiC.ConceptosFiscales) {
            // Asigna identificador único al concepto fiscal
            conceptoF._Identificador = `${conceptoF.IdAgrupador}-${conceptoF.MovimientoFinanza.map(m => m.Id).join(",")}`;
            // Asigna complemento fiscal al concepto
            const alreadyCfdiGroup = this.cfdisConceptosProcesadosList.find(c => c.GrupoCFDI == cfdiC.GrupoCFDI);
            if (alreadyCfdiGroup) {
                const conceptoPrevio = alreadyCfdiGroup.ConceptosFiscales.find(c => c._Identificador == conceptoF._Identificador);
                conceptoF._Complemento = conceptoPrevio?._Complemento || null;
            }
            if (conceptoF.ComplementoRequerido > 0 && !conceptoF._Complemento) {
                // Asigna un nuevo complemento si es requerido y no existe previamente
                conceptoF._Complemento = await Util._CreateComplementoIEDUByAlumnoID(this.config.idAlumno);
            }
            // Asigna todos los IDs de movimientos asociados al cfdi
            conceptoF.MovimientoFinanza.forEach(mov => {
                cfdiC._IdsMovs.push(mov.Id);
            });
        }
        return cfdiC;
    }

    //#endregion

    // ****************************************************************
    // SERVICE CALLS
    // ****************************************************************

    //#region SERVICE CALLS - CONSULTAS Y PROCESAMIENTO DE DATOS

    private ConsultaMovimientosProcesar() {
        const { idEscuela, idAlumno, idsPagos } = this.config;
        const idsMovsSelectedAux = { ...this.dataSelected }
        this.dataSelected = {};

        const firstData = !this.cfdisConceptosProcesadosList.length;

        this.modal.Progress.node()._Visible = true

        _SvFacturaCFDIProcesarConceptosFiscales(idEscuela, idAlumno, idsPagos)
            .then(async (res) => {
                // Evalua respuesta
                if (res.Resultado <= 0) {
                    res.Mensaje = _HttpMsgV2(res)
                    NotificacionV2._Mostrar(res.Mensaje, "ADVERTENCIA")
                } else if (!res.Datos.length) {
                    res.Mensaje = _L("panelfinanzasedocuenta_factura.no_facturables")
                }
                if (res.Mensaje) {
                    this.sinRegistrosComponent._SetText(res.Mensaje)
                }
                this.sinRegistrosComponent._Visible(!!res.Mensaje)

                // Procesa datos de respuesta
                this.cfdisConceptosProcesadosList = await new ArrayV2(...(res.Datos || []))
                    ._MapAwait(async (grupoCFDI: IFacturaCFDIConceptosFiscalesProcesadosAdvanced, i: number) => {
                        await this.PreparaCFDIConceptos(grupoCFDI, (i + 1).toString());
                        for (const conceptoF of grupoCFDI.ConceptosFiscales) {
                            conceptoF.MovimientoFinanza.forEach(mov => {
                                if (!mov.EsFacturable) return;
                                if (idsMovsSelectedAux[grupoCFDI.GrupoCFDI] && idsMovsSelectedAux[grupoCFDI.GrupoCFDI][conceptoF._Identificador]?.has(mov.Id)) {
                                    this.SelectMovsByKeys(grupoCFDI.GrupoCFDI, conceptoF._Identificador, mov.Id, mov.EsFacturable, true);
                                }
                            });
                        }
                        return grupoCFDI;
                    });
                this.pagosInfoListCtrl._SetItems(this.cfdisConceptosProcesadosList);
                this.lblSeleccionadoInfo._refreshText();

                if (this.config.onloadData) {
                    const success: boolean = res.Resultado > 0 && !!res.Datos?.length;
                    this.config.onloadData(firstData, success);
                }

                return !res.Mensaje
            })
            .finally(() => {
                this.modal.Progress.node()._Visible = false
            });
    }
}

export interface ViewMovimientosPagos {
    /** Consulta los conceptos fiscales procesados de los movimientos seleccionados */
    ConsultaCFDIConceptosFiscalesProcesados: () => Promise<IFacturaCFDIConceptosFiscalesProcesadosAdvanced[]>
    /** Valida si hay movimientos seleccionados */
    IsValid: () => Promise<boolean>
    /** Selecciona movimientos específicos por sus IDs */
    SelectMovs: (ids: number[]) => void
    /** Selecciona todos los movimientos facturables */
    SelectAll: () => void
    // /** Promesa que indica si la carga de datos se completó */
    // LoadComplete: Promise<boolean>
    /** Refresca la información de los movimientos */
    ReloadData: () => void
}